import { useEffect, useRef, type ReactNode } from 'react'

import { t, isRTL } from './i18n'
import type { HelpStep } from './micHelp'
import { StepArt } from './MicHelpDialog'
import { useFocusTrap } from './useFocusTrap'
import { CapabilityRetry } from './CapabilityRetry'
import type { CapabilityKind } from './capabilityMedia'

export function HelpDialog({
  title,
  body,
  steps,
  onRetry,
  retryLabel,
  onDismiss,
  retrying = false,
  tone,
  secondary,
  platformToggle,
  icon,
  capabilityKind,
}: {
  title: string
  body?: string
  steps: HelpStep[]
  onRetry: () => void | Promise<void>
  retryLabel: string
  onDismiss: () => void
  retrying?: boolean
  tone: 'prejoin' | 'inroom'
  secondary?: ReactNode
  platformToggle?: { label: string; onToggle: () => void }
  icon?: ReactNode
  capabilityKind?: CapabilityKind
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef)
  const previous = useRef<Element | null>(null)
  useEffect(() => {
    previous.current = document.activeElement
    dialogRef.current?.focus()
    return () => {
      if (previous.current instanceof HTMLElement) previous.current.focus()
    }
  }, [])

  const artStep = steps.find((step) => step.photo) ?? steps[0]
  const dismissLabel = tone === 'prejoin' ? t('mic.help.joinAnyway') : t('mic.help.notNow')

  return (
    <div className="mic-help__scrim" role="presentation">
      <div
        className="mic-help"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-dialog-title"
        dir={isRTL ? 'rtl' : 'ltr'}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={(event) => event.key === 'Escape' && onDismiss()}
      >
        <div className="mic-help__head">
          {icon ? (
            <span className="mic-help__icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <h2 className="mic-help__title" id="help-dialog-title">
            {title}
          </h2>
        </div>
        {body ? <p className="mic-help__lede">{body}</p> : null}
        {steps.length ? (
          <div className="mic-help__demo">
            <StepArt step={artStep} />
            <ol className="mic-help__steps">
              {steps.map((step, i) => (
                <li key={`${step.art}-${i}`}>{step.text}</li>
              ))}
            </ol>
          </div>
        ) : null}
        <div className="mic-help__dock">
          <div className="mic-help__pepc-cradle">
            {capabilityKind && !retrying ? (
              <CapabilityRetry kind={capabilityKind} label={retryLabel} onReady={() => void onRetry()} />
            ) : (
              <button type="button" className="primary-button primary-button--lg" onClick={() => void onRetry()} disabled={retrying}>
                {retrying ? t('mic.help.checking') : retryLabel}
              </button>
            )}
          </div>
          {secondary ?? (
            <button type="button" className="mic-help__link" onClick={onDismiss}>
              {dismissLabel}
            </button>
          )}
        </div>
        {platformToggle ? (
          <p className="mic-help__platform">
            <button type="button" className="mic-help__link" onClick={platformToggle.onToggle}>
              {platformToggle.label}
            </button>
          </p>
        ) : null}
        <p className="mic-help__foot">{t('mic.help.askAdult')}</p>
      </div>
    </div>
  )
}
